import { formatISO } from 'date-fns';
import { useMemo } from 'react';
import { gql, useQuery } from 'urql';

import {
  RangeDate,
  RangeInt,
  RgCraV2ReviewStatus,
  RgCraV2RiskLevel,
} from '@/globalTypes';
import { useQueryParamsWithTime } from '@/hooks';
import { mapVariables } from '@/utils';
import { makeOrderBy, makePagination } from '@/utils/pagination';
import {
  RgRiskAssessmentsQuery,
  RgRiskAssessmentsQueryVariables,
} from './__generated__/useRgRiskAssessment';
import { queryParams } from './queryParams';

type RgCraPlayer = NonNullable<
  NonNullable<
    RgRiskAssessmentsQuery['viewer']['rgRiskAssessments']['edges']
  >[number]
>['node']['players'][number];

export type RgCraEntry = {
  playerGlobalId?: string;
  riskLevel?: RgCraV2RiskLevel;
  calculatedAt?: string | null;
  players?: RgCraPlayer[];
  reviewStatusInfo?: {
    status?: RgCraV2ReviewStatus;
    lastReviewed?: string | null;
  };
  overriddenRiskLevel?: RgCraV2RiskLevel | null;
  score?: number;
  maxScore?: number;
  percentage?: number | null;
  finalScore?: number | null;
  [key: string]: unknown;
};

const QUERY = gql`
  query RgRiskAssessments(
    $playerGlobalId: ID
    $percentage: RangeInt
    $rgCraRiskLevels: [RgCraV2RiskLevel!]
    $rgCraReviewStatus: [RgCraV2ReviewStatus!]
    $calculatedAt: RangeDate
    $lastReviewedAt: RangeDate
    $orderBy: RgCraV2RiskAggregationOrderBy
    $desc: Boolean
    $rgCraRiskTerms: [RgCraV2RiskTerm!]
    $before: String
    $after: String
    $first: Int
    $last: Int
  ) {
    viewer {
      id
      rgRiskAssessments: rgCraV2RiskAssessments(
        playerGlobalId: $playerGlobalId
        percentage: $percentage
        rgCraRiskLevels: $rgCraRiskLevels
        rgCraReviewStatus: $rgCraReviewStatus
        calculatedAt: $calculatedAt
        lastReviewedAt: $lastReviewedAt
        orderBy: $orderBy
        desc: $desc
        rgCraRiskTerms: $rgCraRiskTerms
        before: $before
        after: $after
        first: $first
        last: $last
      ) {
        pageInfo {
          endCursor
          hasNextPage
          startCursor
          hasPreviousPage
        }
        edges {
          node {
            playerGlobalId
            players {
              id
              rawPlayerId
              brand {
                name
              }
            }
            score
            maxScore
            percentage
            finalScore
            overriddenRiskLevel
            riskLevel
            calculatedAt
            reviewStatusInfo {
              status
              lastReviewed
            }
          }
        }
      }
    }
  }
`;

export default function useRgRiskAssessment() {
  const [query, setQuery] = useQueryParamsWithTime(queryParams, {
    fromAndToOptional: true,
  });

  const calculatedAt = useMemo((): RangeDate | undefined => {
    if (query.from && query.to) {
      return {
        from: formatISO(query.from),
        to: formatISO(query.to),
      };
    }

    return undefined;
  }, [query.from, query.to]);

  const lastReviewedAt = useMemo((): RangeDate | undefined => {
    if (query.lastReviewedFrom && query.lastReviewedTo) {
      return {
        from: formatISO(query.lastReviewedFrom),
        to: formatISO(query.lastReviewedTo),
      };
    }
    return undefined;
  }, [query.lastReviewedFrom, query.lastReviewedTo]);

  const percentage = useMemo((): RangeInt | undefined => {
    if (query.percentageFrom || query.percentageTo) {
      return {
        from: query.percentageFrom ?? 0,
        to: query.percentageTo ?? 100,
      };
    }

    return undefined;
  }, [query.percentageFrom, query.percentageTo]);

  const rgCraRiskTerms = useMemo(() => {
    const riskId = query.rgCraRiskTermRiskId;

    if (riskId && query.rgCraRiskTermRiskLevels?.length) {
      return query.rgCraRiskTermRiskLevels.map((riskLevel) => ({
        riskId,
        riskLevel,
      }));
    }

    return undefined;
  }, [query.rgCraRiskTermRiskId, query.rgCraRiskTermRiskLevels]);

  const {
    percentageFrom: _percentageFrom,
    percentageTo: _percentageTo,
    rgCraRiskTermRiskId: _rgCraRiskTermRiskId,
    rgCraRiskTermRiskLevels: _rgCraRiskTermRiskLevels,
    ...queryVariables
  } = query;

  const variables = {
    ...mapVariables(queryVariables),
    calculatedAt,
    lastReviewedAt,
    percentage,
    rgCraRiskTerms,
  };

  const [{ data, fetching }, refresh] = useQuery<
    RgRiskAssessmentsQuery,
    RgRiskAssessmentsQueryVariables
  >({
    query: QUERY,
    variables: variables as RgRiskAssessmentsQueryVariables,
  });

  return {
    refresh,
    fetching,
    defaultFilter: query,
    updateFilter: (values: {}) => setQuery(values, 'replaceIn'),
    ...makeOrderBy(query, setQuery),
    ...makePagination(query, setQuery, data?.viewer.rgRiskAssessments.pageInfo),
    entries: data?.viewer.rgRiskAssessments?.edges?.map(
      (edge): RgCraEntry | undefined => edge?.node,
    ),
  };
}
